import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Header from '@/components/Header';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Star, ShoppingCart, Filter } from 'lucide-react';
import { toast } from 'sonner';

const ShopPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: 'all',
    sport: 'all',
    search: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.category && filters.category !== 'all') params.append('category', filters.category);
      if (filters.sport && filters.sport !== 'all') params.append('sport', filters.sport);
      
      const response = await axios.get(`${API}/products?${params}`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(filters.search.toLowerCase()) ||
    product.brand.toLowerCase().includes(filters.search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="shop-page">
      <SEO
        title="Shop"
        description="Shop premium equipment at Sun Kamal Academy. Chess boards, drums, yoga mats and more for your learning journey."
        path="/shop"
      />
      <Header />

      {/* Page Header */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-10 sm:py-16 pt-24 sm:pt-28">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-2 sm:mb-4" data-testid="page-title">Academy Shop</h1>
          <p className="text-base sm:text-lg md:text-xl opacity-90">Premium equipment for your learning journey</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8 md:py-12">
        {/* Filters */}
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm mb-6 sm:mb-8" data-testid="filters-section">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-base sm:text-lg font-semibold">Filters</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <Input
              placeholder="Search products..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              data-testid="search-input"
            />
            <Select value={filters.category} onValueChange={(value) => setFilters({ ...filters, category: value })}>
              <SelectTrigger data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Chess">Chess</SelectItem>
                <SelectItem value="Checkers">Checkers</SelectItem>
                <SelectItem value="Drums">Drums / Tabla</SelectItem>
                <SelectItem value="Yoga">Yoga</SelectItem>
                <SelectItem value="Spiritual Books">Spiritual Books</SelectItem>
                <SelectItem value="Spiritual Health">Spiritual Health</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.sport} onValueChange={(value) => setFilters({ ...filters, sport: value })}>
              <SelectTrigger data-testid="sport-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Chess">Chess</SelectItem>
                <SelectItem value="Checkers">Checkers</SelectItem>
                <SelectItem value="Drums">Drums / Tabla</SelectItem>
                <SelectItem value="Yoga">Yoga</SelectItem>
                <SelectItem value="Spirituality">Spirituality</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={fetchProducts} className="btn-primary" data-testid="apply-filters-btn">
              Apply Filters
            </Button>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-16 sm:py-20">Loading products...</div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16 sm:py-20">No products found</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6" data-testid="products-grid">
            {filteredProducts.map((product, index) => (
              <Card
                key={product.id}
                className="overflow-hidden hover:shadow-xl transition cursor-pointer"
                onClick={() => navigate(`/products/${product.id}`)}
                data-testid={`product-card-${index}`}
              >
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-full h-36 sm:h-48 object-cover"
                  loading="lazy"
                />
                <div className="p-3 sm:p-4">
                  <div className="text-xs sm:text-sm text-gray-500 mb-1">{product.brand}</div>
                  <h3 className="font-bold text-sm sm:text-base mb-1 sm:mb-2 line-clamp-2" data-testid={`product-name-${index}`}>{product.name}</h3>
                  <div className="flex items-center gap-1 mb-2 sm:mb-3">
                    <Star className="w-3 h-3 sm:w-4 sm:h-4 fill-yellow-400 text-yellow-400" />
                    <span className="text-xs sm:text-sm">{product.rating.toFixed(1)}</span>
                    <span className="text-xs sm:text-sm text-gray-500 ml-1 sm:ml-2 truncate">{product.category}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-lg sm:text-2xl font-bold text-blue-600">${product.price}</div>
                    <Button size="sm" className="btn-primary p-2 sm:p-2.5" data-testid={`view-product-btn-${index}`}>
                      <ShoppingCart className="w-4 h-4" />
                    </Button>
                  </div>
                  {product.stock < 10 && product.stock > 0 && (
                    <div className="text-xs sm:text-sm text-orange-600 mt-1 sm:mt-2">Only {product.stock} left!</div>
                  )}
                  {product.stock === 0 && (
                    <div className="text-xs sm:text-sm text-red-600 mt-1 sm:mt-2">Out of stock</div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopPage;
